import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

/**
 * The ISO 3166-1 country list, localised.
 *
 * Two pieces, on purpose:
 *
 * - `i18n-iso-countries` supplies the canonical alpha-2 code list and the English names.
 *   Only the `en` pack is registered; registering its other 77 locale packs would put
 *   well over a megabyte of country names into the bundle for a field most students set
 *   once.
 * - `Intl.DisplayNames` supplies the localised names, from the CLDR data the browser
 *   already carries. That covers every locale the app runs in — including the 35 the
 *   library ships no pack for — at no bundle cost.
 *
 * Where `Intl.DisplayNames` is missing or has no data for a locale, names fall back to
 * English rather than to a bare code.
 */
// resolveJsonModule types the import as its literal shape, which is structurally the
// LocaleData the library wants but not nominally it - hence the cast, not an `any`.
countries.registerLocale(en);

export interface ICountryOption {
  code: string;
  name: string;
}

const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

function displayNames(locale: string): Intl.DisplayNames | null {
  if (!displayNamesCache.has(locale)) {
    let instance: Intl.DisplayNames | null = null;

    try {
      instance = new Intl.DisplayNames([locale], { type: 'region' });
    } catch {
      instance = null;
    }

    displayNamesCache.set(locale, instance);
  }

  return displayNamesCache.get(locale) ?? null;
}

export function getCountryCodes(): string[] {
  return Object.keys(countries.getAlpha2Codes());
}

export function isCountryCode(code: string | null | undefined): boolean {
  return Boolean(code) && countries.isValid(code as string);
}

export function getCountryName(
  alpha2: string | null | undefined,
  locale: string
): string | null {
  if (!alpha2 || !countries.isValid(alpha2)) return null;

  const code = alpha2.toUpperCase();
  // `of` answers with the code itself when the browser has no name for it, which reads
  // as a bug in the list rather than as a missing translation.
  const localised = displayNames(locale)?.of(code);
  if (localised && localised !== code) return localised;

  return countries.getName(code, 'en') ?? null;
}

/**
 * Every country, named for this reader and collated the way they read. Sorting the raw
 * strings puts 'Åland' after 'Zimbabwe', which is not where a Norwegian looks for it.
 */
export function getCountryOptions(locale: string): ICountryOption[] {
  const collator = new Intl.Collator(locale);

  return getCountryCodes()
    .map((code) => ({ code, name: getCountryName(code, locale) ?? code }))
    .sort((a, b) => collator.compare(a.name, b.name));
}

/**
 * Accent- and case-insensitive, so a Norwegian searching for Åland finds it by typing
 * "aland" and a French reader finds "Émirats" by typing "emirats".
 */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * The countries matching what the student has typed, best match first.
 *
 * Ranked rather than merely filtered: typing "ni" should offer Nicaragua and Niger
 * before Bosnia and Herzegovina, and typing a code should put that country at the top
 * even where the same two letters start a dozen names.
 */
export function filterCountryOptions(
  options: ICountryOption[],
  query: string
): ICountryOption[] {
  const needle = fold(query.trim());
  if (!needle) return options;

  const CODE = 0;
  const STARTS_WITH = 1;
  const CONTAINS = 2;

  return (
    options
      .map((option) => {
        const name = fold(option.name);

        if (option.code.toLowerCase() === needle) return { option, rank: CODE };
        if (name.startsWith(needle)) return { option, rank: STARTS_WITH };
        if (name.includes(needle)) return { option, rank: CONTAINS };

        return null;
      })
      .filter(
        (match): match is { option: ICountryOption; rank: number } => match !== null
      )
      // Stable, so names keep the locale's collation order inside each rank.
      .sort((a, b) => a.rank - b.rank)
      .map(({ option }) => option)
  );
}
